// ---------------------------------------------------------------------------
// Vendor payment terms model
// ---------------------------------------------------------------------------
// Each vendor (economist, business plan writer, securities attorney,
// immigration attorney, fund administrator) has a list of "installments"
// that say *when* a portion of its fee is due relative to a project anchor
// event. The engine in server/finance/master-pl.ts expands these into dated
// cash-basis ledger entries (and a parallel accrual entry) for every
// vendor cost line on every project.
//
// Anchors:
//   engagement       -> when the vendor was engaged for this role (start of
//                       the role's work window). Sourced from the i956f
//                       forecast step.startDate for that role.
//   delivery         -> when the vendor's deliverable lands (end of the
//                       role's work window). Sourced from the i956f
//                       forecast step.endDate for that role.
//   marketing_ready  -> first-close proxy: the marketing-ready date.
//
// Recurring components (fund admin monthly + annual storage) are not
// expressed via installments; they are computed structurally by the engine
// from vendor rate-card fields.

import { z } from "zod";

export const PAYMENT_ANCHORS = [
  "engagement",
  "delivery",
  "marketing_ready",
] as const;
export type PaymentAnchor = (typeof PAYMENT_ANCHORS)[number];

export const PAYMENT_ANCHOR_LABEL: Record<PaymentAnchor, string> = {
  engagement: "On engagement",
  delivery: "On delivery",
  marketing_ready: "At marketing-ready / first close",
};

export type PaymentInstallment = {
  // Portion of the base fee paid in this installment, 0-100. Installments
  // for a given vendor role should sum to 100.
  portionPct: number;
  anchor: PaymentAnchor;
  // Optional offset in days from the anchor (e.g., +30 for net-30 terms).
  offsetDays?: number;
};

export const paymentInstallmentSchema = z.object({
  portionPct: z.number().min(0).max(100),
  anchor: z.enum(PAYMENT_ANCHORS),
  offsetDays: z.number().int().optional(),
});

export const paymentTermsSchema = z.array(paymentInstallmentSchema);

// Vendor-role keys for default-term lookup. A single attorney record can
// service both securities and immigration roles, so attorneys carry one
// set of payment terms applied to whichever role they are filling on a
// project (default: on delivery).
export type DefaultTermKey =
  | "economist"
  | "business_plan_writer"
  | "securities_attorney"
  | "immigration_attorney"
  | "fund_admin_onboarding";

export const DEFAULT_PAYMENT_TERMS: Record<DefaultTermKey, PaymentInstallment[]> = {
  economist: [{ portionPct: 100, anchor: "engagement" }],
  business_plan_writer: [
    { portionPct: 50, anchor: "engagement" },
    { portionPct: 50, anchor: "delivery" },
  ],
  securities_attorney: [{ portionPct: 100, anchor: "delivery" }],
  immigration_attorney: [{ portionPct: 100, anchor: "delivery" }],
  fund_admin_onboarding: [{ portionPct: 100, anchor: "engagement" }],
};

export function resolvePaymentTerms(
  vendorTerms: PaymentInstallment[] | null | undefined,
  defaultKey: DefaultTermKey,
): PaymentInstallment[] {
  if (Array.isArray(vendorTerms) && vendorTerms.length > 0) {
    // Normalize: ensure portions sum to 100. If they don't, scale them so
    // they do — keeps the grand total honest even if the terms are mis-set.
    const total = vendorTerms.reduce((s, i) => s + (i.portionPct || 0), 0);
    if (total <= 0) return DEFAULT_PAYMENT_TERMS[defaultKey];
    if (Math.abs(total - 100) < 0.01) return vendorTerms;
    return vendorTerms.map(i => ({ ...i, portionPct: (i.portionPct / total) * 100 }));
  }
  return DEFAULT_PAYMENT_TERMS[defaultKey];
}

// ---------------------------------------------------------------------------
// Vendor categories used to group rows in the master P&L
// ---------------------------------------------------------------------------
export const VENDOR_CATEGORIES = [
  "economist",
  "business_plan",
  "securities",
  "immigration",
  "fund_admin",
] as const;
export type VendorCategory = (typeof VENDOR_CATEGORIES)[number];

export const VENDOR_CATEGORY_LABEL: Record<VendorCategory, string> = {
  economist: "Economist",
  business_plan: "Business Plan",
  securities: "Legal — Securities",
  immigration: "Legal — Immigration",
  fund_admin: "Fund Admin",
};
